import AddIcon from '@mui/icons-material/Add';
import ApiIcon from '@mui/icons-material/Api';
import AutorenewIcon from '@mui/icons-material/Autorenew';
import DeleteIcon from '@mui/icons-material/Delete';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import HistoryIcon from '@mui/icons-material/History';
import { Badge, Button, Chip, Dialog, DialogActions, DialogContent, IconButton, Tooltip } from "@mui/material";
import classNames from 'classnames';
import { format } from 'date-fns';
import React, { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { createUserApiToken, deleteUserApiToken, getUserApiTokens, regenerateUserApiToken } from '../api/auth';
import { AccountContext } from "../context/account";
import { ApiAccessToken } from "../core/global";
import { tokensPermissionsModal } from '../data/content';
import { tokenShape } from "../data/form-shapes";
import { trackEvent } from '../helpers/tracking';
import tableStyles from "../styles/components/table.module.scss";
import { CopyButton } from './copy-button';
import { ErrorMessage, WarningMessage } from './error-message';
import { CreateForm } from "./form-components";
import { InfoModal } from './info-modal';
import Loader from "./loader";
import { ModalTitle } from "./modal-title";
import { TokensHistoryModal } from './tokens-history-modal';

interface TokensModalProps {
  onClose: () => void
}

type RowAction = 'delete' | 'regenerate' | null;

// Pulls the raw token out of whatever response shape the API wrapper returns.
const extractToken = (resp: any): string | null =>
  resp?.token || resp?.response?.token || resp?.data?.token || null;

export const TokensModal = ({
  onClose,
}: TokensModalProps) => {
  const [apiError, setApiError] = useState<string[] | null>(null)
  const [apiTokens, setApiTokens] = useState<ApiAccessToken[] | null>(null)
  const [editedRowId, setEditedRowId] = useState<number | string | null>(null)
  const [rowAction, setRowAction] = useState<RowAction>(null)
  const [historyRowId, setHistoryRowId] = useState<number | string | null>(null)
  const [createNewToken, setCreateNewToken] = useState(false)
  const [showTokenPermissionsModal, setShowTokenPermissionsModal] = useState(false)
  // The one-time-visible raw token (from create or regenerate). Cleared on dismiss.
  const [newToken, setNewToken] = useState<string | null>(null)

  const {
    account,
  } = useContext(AccountContext);

  const getTokens = useCallback(async () => {
    const execute = async () => {
      const result = await getUserApiTokens()
      const { error, response, message } = result

      if (error === 200 && response) {
        setApiError(null)
        const tokens = response?.tokens || []
        setApiTokens(tokens)
      } else {
        setApiError([message || "An error occurred while fetching your API tokens."])
      }
    }
    execute()
  }, [])

  useEffect(() => {
    getTokens();
  }, [])

  const organisationsThatCanPurchase = useMemo(() => {
    if (account) {
      const { organisation } = account
      if (organisation && Array.isArray(organisation)) {
        const found = organisation.filter(org => {
          const { super_subscriber, members_pre_authorised, role } = org
          return super_subscriber &&
            (members_pre_authorised || (role === 'admin' || role === 'super-admin'))
        })
        if (found) {
          return found
        }
      }
    }
    return []
  }, [account])

  useEffect(() => {
    if (account.uid) {
      trackEvent("manage-tokens-modal", { uid: account.uid })
    }
  }, [account.uid])

  const resetRowAction = () => {
    setEditedRowId(null)
    setRowAction(null)
  }

  const handleConfirmAction = async (id: number | string) => {
    try {
      setApiError(null)
      if (rowAction === 'delete') {
        const res = await deleteUserApiToken({ id })
        const { error, message } = res || {}
        if (error !== 200) {
          setApiError([`${message || 'Could not delete token.'}`])
        } else {
          getTokens()
        }
      } else if (rowAction === 'regenerate') {
        const res = await regenerateUserApiToken({ id })
        const { error, message } = res || {}
        if (error !== 200) {
          setApiError([`${message || 'Could not regenerate token.'}`])
        } else {
          const token = extractToken(res)
          if (token) setNewToken(token)
          getTokens()
        }
      }
    } finally {
      resetRowAction()
    }
  }

  return (
    <>
    <Dialog
      open
      onClose={onClose}
      maxWidth="md"
      fullWidth
    >
      <ModalTitle onClose={onClose}>
        <Badge
          badgeContent={'Beta'}
          color="warning"
          anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        >
        Manage your API access tokens
        </Badge>
      </ModalTitle>
      <DialogContent
          sx={{ display: 'flex', flexDirection: 'column', gap: 2, width: '100%' }}
        >

        <ErrorMessage message={apiError} />
        {!apiTokens && <Loader center />}

        <p>You can manage your API access tokens below. Read the <a href={`${import.meta.env.VITE_API_DOCS}`} target='_blank'>API Documentation</a> for implementation and code samples.</p>

        <WarningMessage message={`For your security, a token's value is shown only once, at the moment it's created. It can't be retrieved later — if you lose a token, regenerate it to get a new value.`} />

        {apiTokens && apiTokens.length === 0 &&
          <>
            <WarningMessage icon={<ApiIcon />} message={`You don't have any API access tokens.`} />
          </>
        }
        {apiTokens && apiTokens.length > 0 &&
          <>
            <table className={tableStyles.table}>
              <thead>
                <tr>
                  <th>Created</th>
                  <th>Expiry</th>
                  <th>
                    <Badge
                      className={tableStyles.badge}
                      badgeContent={"?"}
                      color="info"
                      onClick={() => { setShowTokenPermissionsModal(true) }}
                    >
                      Permission
                    </Badge>
                  </th>
                  <th>Token</th>
                  <th style={{ minWidth: '170px' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {apiTokens.map((token) => {
                  const { id, created_at, company_id, expiry_at, full_access } = token;
                  const useCompanyName = company_id === null ? "Personal account only" : organisationsThatCanPurchase.find(org => org.id === company_id)?.name || "Unknown";
                  const isEditTokenRow = editedRowId !== null && editedRowId === id
                  const hasExpired = expiry_at !== null && new Date(expiry_at) < new Date()
                  return (
                    <React.Fragment key={id}>
                      <tr key={id} className={classNames({
                        [tableStyles.deleting]: isEditTokenRow && rowAction === 'delete'
                      })}>
                        <td className={tableStyles.date}>{format(new Date(created_at), 'dd/MM/yyyy')}</td>
                        {hasExpired ?
                        <td><Chip onClick={() => {}} label={`Expired`} variant="filled" color="error" /></td>
                        :
                        <td className={tableStyles.date}>{expiry_at === null ? "Never" : format(new Date(expiry_at), 'dd/MM/yyyy')}</td>
                        }
                        <td>
                          <strong>{full_access ? "Full access" : "Read only"}</strong>
                          {full_access && <>
                            <br />
                            <Chip onClick={() => {}} label={useCompanyName} variant="filled" color="default" />
                          </>}
                        </td>
                        <td>
                          <Tooltip title="For security, token are only shown once, when created.">
                            <span style={{ letterSpacing: '2px', color: 'var(--mui-palette-text-disabled, #9e9e9e)' }}>
                              ••••••••••
                            </span>
                          </Tooltip>
                        </td>
                        <td className={tableStyles.actions} style={{ minWidth: '170px' }}>
                          <>
                            <Tooltip title="View usage history">
                              <IconButton onClick={() => { setHistoryRowId(id) }}>
                                <HistoryIcon />
                              </IconButton>
                            </Tooltip>

                            <Tooltip title="Regenerate token">
                              <IconButton onClick={() => {
                                setEditedRowId(id)
                                setRowAction('regenerate')
                              }}>
                                <AutorenewIcon />
                              </IconButton>
                            </Tooltip>

                            <Tooltip title="Delete token">
                              <IconButton onClick={() => {
                                setEditedRowId(id)
                                setRowAction('delete')
                              }}>
                                <DeleteIcon />
                              </IconButton>
                            </Tooltip>

                            {isEditTokenRow && rowAction &&
                              <>
                              
                                <div>
                                {rowAction === 'regenerate' &&
                                  <WarningMessage message={`The current token will stop working immediately and a new value will be shown once.`} />
                                }
                                  <Button
                                    size='small'
                                    color={rowAction === 'delete' ? 'error' : 'warning'}
                                    startIcon={rowAction === 'delete' ? <DeleteForeverIcon /> : <AutorenewIcon />}
                                    onClick={() => handleConfirmAction(id)}
                                  >
                                    {rowAction === 'delete' ? 'Remove' : 'Regenerate'}
                                  </Button>
                                  <Button
                                    size='small'
                                    color="success"
                                    onClick={resetRowAction}
                                  >
                                    Cancel
                                  </Button>
                                </div>
                              </>
                            }
                          </>
                        </td>
                      </tr>
                    </React.Fragment>
                  )
                })}
              </tbody>
            </table>
          </>
        }
        <Button
          sx={{ maxWidth: "240px" }}
          variant="contained"
          startIcon={<AddIcon sx={{ color: '#ffffff' }} />}
          onClick={() => { setCreateNewToken(true) }}
        >
          Create token
        </Button>
      </DialogContent>
      <DialogActions sx={{ justifyContent: 'flex-end', pb: 3, px: 3 }}>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>

    {/* One-time token reveal — shown after create or regenerate */}
    {newToken &&
      <Dialog open maxWidth="sm" fullWidth onClose={() => setNewToken(null)}>
        <ModalTitle onClose={() => setNewToken(null)}>
          Copy your new API token
        </ModalTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <WarningMessage message={`This is the only time this token will be shown. Copy it now and store it somewhere safe — for security, we don't keep a copy and can't show it again. If you lose it, regenerate the token to get a new value.`} />
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            padding: '12px 16px',
            borderRadius: 8,
            background: 'var(--mui-palette-action-hover, #f5f5f5)',
            wordBreak: 'break-all',
          }}>
            <code style={{ flex: 1, fontFamily: 'monospace' }}>{newToken}</code>
            <CopyButton size='medium' content={newToken} />
          </div>
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'flex-end', pb: 3, px: 3 }}>
          <Button variant="contained" onClick={() => setNewToken(null)}>
            I've copied it
          </Button>
        </DialogActions>
      </Dialog>
    }

    {historyRowId &&
      <TokensHistoryModal
        onClose={() => { setHistoryRowId(null) }}
        tokenId={historyRowId}
      />
    }
    {createNewToken && <CreateForm
      title={'Create a new API access token'}
      intro={`You can create a new API access token below. Set an expiry date to limit how long it stays valid, or leave it blank. When you save, the token is generated and **shown to you once** — copy it immediately, as it cannot be retrieved again.`}
      isModal
      submitButtonLabel={'Create token'}
      defaltIsEdited
      shape={organisationsThatCanPurchase.length > 0 ? [
          ...tokenShape,
          {
            key: "full_access",
            title: "Full access",
            type: 'checkbox',
            note: "Using this token, you can make purchases on behalf of your account. Please keep it safe and do not share it with anyone.",
          },
          {
            key: "company_id",
            options: [
              { key: null, value: 'Personal account' },
              ...organisationsThatCanPurchase.map(org => ({ key: org.id, value: org.name }))
            ],
            showWhenTrue: ['full_access'],
            required: true,
            title: "Associate full access with",
            note: "You can associate this token with your organisation. This will allow you to use the token for your organisation's API access to purchase data.",
          }
        ]
      :
        [...tokenShape]
      }
      successMessage={"Your new API token has been created. Copy it from the dialog — it won't be shown again."}
      defaultValues={{}}
      onCancel={() => { setCreateNewToken(false) }}
      onSubmit={async (vals) => {
        const result = await createUserApiToken({ ...vals })
        const token = extractToken(result)
        if (token) setNewToken(token)   // capture the one-time value
        return result                   // hand back to CreateForm for success/error handling
      }}
      onSuccess={() => {
        getTokens()
        setCreateNewToken(false)
      }}
    />
    }
    <InfoModal
      open={showTokenPermissionsModal}
      title={tokensPermissionsModal.title}
      content={tokensPermissionsModal.content}
      maxWidth="sm"
      onClose={() => { setShowTokenPermissionsModal(false) }}
    />
  </>
  )
}